export type TranscriptWord = {
  start?: number;
  start_time?: number;
  end?: number;
  end_time?: number;
};

export type Segment = { start: number; end: number };

/**
 * Groups transcription words into padded segments, dropping silent gaps —
 * the pure core of the editor's "decoupage" (remove-silence) action.
 *
 * Words within `silenceGap` seconds of each other are merged into one
 * segment; a larger gap (silence) starts a new segment. Each resulting
 * segment is padded by `padding` seconds and clamped to [clipStart, clipEnd].
 * Returns an empty array when no words fall inside the clip bounds (caller
 * should fall back to the original single segment in that case).
 *
 * NOTE: This algorithm is mirrored in the backend
 * (`autoclipper-backend/src/lib/groupTranscriptSegments.js`). Keep both in
 * sync — the backend applies it to auto-generated clips at curation time,
 * the frontend editor applies it on demand via `handleDecoupClip`.
 */
export function groupTranscriptSegments({
  words,
  clipStart,
  clipEnd,
  silenceGap,
  padding,
}: {
  words: TranscriptWord[];
  clipStart: number;
  clipEnd: number;
  silenceGap: number;
  padding: number;
}): Segment[] {
  const groups: Segment[] = [];
  for (const w of words) {
    const ws = w.start ?? w.start_time ?? 0;
    const we = w.end ?? w.end_time ?? ws;
    if (we <= clipStart || ws >= clipEnd) continue;
    const start = Math.max(ws, clipStart);
    const end = Math.min(we, clipEnd);
    const last = groups[groups.length - 1];
    if (last && start - last.end <= silenceGap) {
      last.end = Math.max(last.end, end);
    } else {
      groups.push({ start, end });
    }
  }
  return groups.map((g) => ({
    start: Math.max(clipStart, g.start - padding),
    end: Math.min(clipEnd, g.end + padding),
  }));
}
